// frontend/src/pages/admin/EditAboutUsPage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getAllSettings, updateSettings, uploadGeneralImage } from '@/api/settingService';
import { type SiteSetting, type SettingUpdatePayload, type SettingValueType } from '@/types';
import { toast } from 'sonner';
import { 
    Loader2, Save, FileText, UsersIcon, Zap, TargetIcon, 
    LightbulbIcon, UploadCloud, Copy, CheckCircle,
    LayoutDashboard
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Define the keys for "About Us" settings we'll manage
const aboutUsSettingKeys = [
  "about_us_page_title", "about_us_page_subtitle",
  "about_us_mission_title", "about_us_mission_p1", "about_us_mission_p2", "about_us_mission_image_url",
  "about_us_story_title", "about_us_story_content",
  "about_us_values_title", "about_us_core_values_json",
  "about_us_team_title", "about_us_team_members_json",
] as const;

type AboutUsSettingKey = typeof aboutUsSettingKeys[number];

// Helper to determine which value field to use based on key
const getValueFieldForKey = (key: AboutUsSettingKey): 'value_str' | 'value_text' | 'value_json' => {
  if (key.endsWith("_json")) return 'value_json';
  if (key === 'about_us_page_subtitle' || 
      key === 'about_us_mission_p1' || 
      key === 'about_us_mission_p2' || 
      key === 'about_us_story_content') return 'value_text';
  return 'value_str';
};

// Define which setting keys represent image URLs that can be uploaded directly within their field
const directImageUploadKeys: AboutUsSettingKey[] = ["about_us_mission_image_url"]; 

const EditAboutUsPage = () => {
  const [settings, setSettings] = useState<Record<AboutUsSettingKey, string>>({} as Record<AboutUsSettingKey, string>);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<SiteSetting[]>([]);
  
  // State for direct image uploads linked to specific setting keys
  const [selectedDirectFiles, setSelectedDirectFiles] = useState<Record<string, File | null>>({});
  const [isDirectUploading, setIsDirectUploading] = useState<Record<string, boolean>>({});

  // State for the general image upload utility
  const [utilityFile, setUtilityFile] = useState<File | null>(null);
  const [isUtilityUploading, setIsUtilityUploading] = useState(false);
  const [utilityImageUrl, setUtilityImageUrl] = useState<string | null>(null);
  const [utilityImageCopied, setUtilityImageCopied] = useState(false);


  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const allDbSettings = await getAllSettings(); 
        setOriginalSettings(allDbSettings);
        const initialFormState = {} as Record<AboutUsSettingKey, string>;
        
        aboutUsSettingKeys.forEach(key => {
          const setting = allDbSettings.find(s => s.key === key);
          const valueField = getValueFieldForKey(key);
          let value = '';
          if (setting) {
            // --- THIS IS THE FIX ---
            if (valueField === 'value_json') {
                const rawJsonValue = setting.value_json;
                let parsedJson: any;

                if (typeof rawJsonValue === 'string') {
                    try {
                        parsedJson = JSON.parse(rawJsonValue);
                    } catch (e) {
                        console.error(`Invalid JSON string for key ${key}:`, rawJsonValue);
                        parsedJson = []; // Default to empty array on parse error
                    }
                } else if (rawJsonValue) {
                    parsedJson = rawJsonValue; // It's already an object/array
                } else {
                    parsedJson = []; // It's null or undefined
                }
                // Stringify with pretty-printing for the textarea
                value = JSON.stringify(parsedJson, null, 2);
            } else {
              value = (setting[valueField] as string) || '';
            }
            // --- END OF FIX ---
          } else if (valueField === 'value_json') { 
            value = '[]'; 
          }
          initialFormState[key] = value;
        });
        setSettings(initialFormState);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast.error("Failed to load About Us settings.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (key: AboutUsSettingKey, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Handler for file input change for direct image fields
  const handleDirectFileChange = (key: AboutUsSettingKey, event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedDirectFiles(prev => ({ ...prev, [key]: event.target.files![0] }));
    } else {
      setSelectedDirectFiles(prev => ({ ...prev, [key]: null }));
    }
  };

  // Handler for uploading images linked directly to a setting key
  const handleDirectImageUpload = async (key: AboutUsSettingKey) => {
    const fileToUpload = selectedDirectFiles[key];
    if (!fileToUpload) {
      toast.error("Please select an image file first.");
      return;
    }
    setIsDirectUploading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await uploadGeneralImage(fileToUpload);
      if (response.image_url) {
        setSettings(prev => ({ ...prev, [key]: response.image_url }));
        toast.success(`Image for ${key.replace(/_/g, ' ')} uploaded! URL updated.`);
        setSelectedDirectFiles(prev => ({ ...prev, [key]: null })); 
      } else {
        toast.error("Upload successful, but no image URL was returned.");
      }
    } catch (error: any) {
      console.error(`Failed to upload image for ${key}:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to upload image.`;
      toast.error(errorMsg);
    } finally {
      setIsDirectUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Handler for the general utility image uploader
  const handleUtilityFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUtilityFile(event.target.files[0]);
      setUtilityImageUrl(null); // Clear previous URL
      setUtilityImageCopied(false);
    } else {
      setUtilityFile(null);
    }
  };

  const handleUtilityImageUpload = async () => {
    if (!utilityFile) {
      toast.error("Please select an image file to upload.");
      return;
    }
    setIsUtilityUploading(true);
    setUtilityImageUrl(null);
    setUtilityImageCopied(false);
    try {
      const response = await uploadGeneralImage(utilityFile);
      if (response.image_url) {
        setUtilityImageUrl(response.image_url);
        toast.success("Image uploaded successfully! You can now copy the URL.");
      } else {
        toast.error("Upload successful, but no image URL was returned.");
      }
    } catch (error: any) {
      console.error("Failed to upload utility image:", error);
      const errorMsg = error.response?.data?.detail || "Failed to upload image.";
      toast.error(errorMsg);
    } finally {
      setIsUtilityUploading(false);
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setUtilityImageCopied(true);
      toast.success("Image URL copied to clipboard!");
      setTimeout(() => setUtilityImageCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy URL: ', err);
      // Fallback for older browsers or if navigator.clipboard is not available (e.g. in non-secure contexts)
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setUtilityImageCopied(true);
        toast.success("Image URL copied to clipboard! (fallback method)");
        setTimeout(() => setUtilityImageCopied(false), 2000);
      } catch (execErr) {
        toast.error('Failed to copy URL. Please copy it manually.');
        console.error('Fallback copy failed: ', execErr);
      }
      document.body.removeChild(textArea);
    });
  };


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    toast.info("Saving About Us content...");

    const settingsToUpdate: SettingUpdatePayload[] = [];
    for (const key of aboutUsSettingKeys) {
        const typedKey = key as AboutUsSettingKey; 
        const valueField = getValueFieldForKey(typedKey);
        let valueToSave: any = settings[typedKey];
        let valueType: SettingValueType;
        
         // Determine value type and parse JSON if necessary
        if (valueField === 'value_json') {
            try { 
                valueToSave = JSON.parse(settings[typedKey] || '[]'); 
                valueType = 'json';
            } 
            catch (jsonError) {
                toast.error(`Invalid JSON format for ${typedKey}. Please correct it.`);
                setIsSaving(false); return;
            }
        } else if (valueField === 'value_text') {
            valueType = 'text';
        } else {
            valueType = 'string';
        }
        
        const originalSetting = originalSettings.find(s => s.key === typedKey);
        let originalValueToCompare = '';
        if (originalSetting) {
            if (valueField === 'value_json') {
                originalValueToCompare = originalSetting.value_json ? JSON.stringify(originalSetting.value_json, null, 2) : '[]';
            } else { originalValueToCompare = (originalSetting[valueField] as string) || ''; }
        } else if (valueField === 'value_json') { originalValueToCompare = '[]';}

        const currentValueToCompare = valueField === 'value_json' ? JSON.stringify(JSON.parse(settings[typedKey]), null, 2) : valueToSave;

        if (currentValueToCompare !== originalValueToCompare) {
             const payload: SettingUpdatePayload = { 
                 key: typedKey, 
                 value_type: valueType,
                 [valueField]: valueToSave 
             };
             settingsToUpdate.push(payload);
        }
    }
    
    if (settingsToUpdate.length === 0) {
        toast.info("No changes to save.");
        setIsSaving(false); return;
    }

    try {
      await updateSettings(settingsToUpdate);
      toast.success("About Us content updated successfully!");
       const allDbSettings = await getAllSettings(); 
       setOriginalSettings(allDbSettings);
        const updatedFormState = {} as Record<AboutUsSettingKey, string>;
        aboutUsSettingKeys.forEach(k => {
            const setting = allDbSettings.find(s => s.key === k);
            const valField = getValueFieldForKey(k);
            if (setting) {
                if (valField === 'value_json') {
                    // Re-apply the same parsing logic to the newly fetched data
                    const rawJson = setting.value_json;
                    let parsed;
                    if (typeof rawJson === 'string') {
                        try { parsed = JSON.parse(rawJson); } catch { parsed = []; }
                    } else { parsed = rawJson || []; }
                    updatedFormState[k] = JSON.stringify(parsed, null, 2);
                } else { updatedFormState[k] = (setting[valField] as string) || '';}
            } else if (valField === 'value_json') { updatedFormState[k] = '[]';
            } else { updatedFormState[k] = '';}
        });
        setSettings(updatedFormState);
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update About Us content.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderFormField = (key: AboutUsSettingKey, title: string, description?: string, placeholder?: string, type: 'input' | 'textarea' | 'json_textarea' = 'input') => {
    const isDirectImageField = directImageUploadKeys.includes(key);
    return (
      <div key={key} className="space-y-2">
        <Label htmlFor={key} className="text-lg font-medium">{title}</Label>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
        
        {isDirectImageField ? (
          <div className="space-y-3 p-4 border rounded-md bg-muted/20">
            <Label htmlFor={key} className="text-base font-normal text-muted-foreground">Image URL</Label>
            <Input
              id={key}
              type="text" 
              value={settings[key] || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={placeholder || `Enter image URL or upload below...`}
              className="border-border focus:border-primary focus:ring-primary bg-background"
            />
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <Label htmlFor={`${key}-direct-file`} className="text-sm whitespace-nowrap sr-only">Select image for {title}:</Label>
              <Input
                id={`${key}-direct-file`}
                type="file"
                accept="image/*"
                onChange={(e) => handleDirectFileChange(key, e)}
                className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-input file:bg-background file:text-sm file:font-medium file:text-foreground hover:file:bg-accent hover:file:text-accent-foreground w-full sm:w-auto"
              />
              <Button 
                type="button" 
                onClick={() => handleDirectImageUpload(key)} 
                disabled={!selectedDirectFiles[key] || !!isDirectUploading[key]}
                size="sm"
                variant="outline"
                className="w-full sm:w-auto"
              >
                {isDirectUploading[key] ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                Upload to Field
              </Button>
            </div>
            {settings[key] && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">Current Image Preview:</p>
                <img 
                    src={settings[key]} 
                    alt={`${title} preview`} 
                    className="max-w-xs max-h-40 rounded border p-1 object-contain bg-white" 
                    onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        const errorMsgContainer = target.parentNode?.querySelector('.preview-error-msg');
                        if (errorMsgContainer) errorMsgContainer.remove(); // Remove old error if any
                        const errorMsg = document.createElement('p');
                        errorMsg.textContent = 'Preview unavailable or broken link.';
                        errorMsg.className = 'text-xs text-destructive preview-error-msg';
                        target.parentNode?.insertBefore(errorMsg, target.nextSibling);
                    }}
                />
              </div>
            )}
          </div>
        ) : type === 'textarea' || type === 'json_textarea' ? (
          <Textarea
            id={key}
            value={settings[key] || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={placeholder || `Enter ${title.toLowerCase()}...`}
            rows={type === 'json_textarea' ? 8 : (key === 'about_us_story_content' ? 10 : 3)}
            className="border-border focus:border-primary focus:ring-primary"
          />
        ) : (
          <Input
            id={key}
            type="text"
            value={settings[key] || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={placeholder || `Enter ${title.toLowerCase()}...`}
            className="border-border focus:border-primary focus:ring-primary"
          />
        )}
        {type === 'json_textarea' && (
          <p className="text-xs text-muted-foreground">
            {'Enter valid JSON. For lists, use an array of objects e.g., `[{"key": "value"}]`.'}
          </p>
        )}
      </div>
    );
  };

  const handleDiscardChanges = () => {
    const initialFormState = {} as Record<AboutUsSettingKey, string>;
    originalSettings.forEach(setting => {
        if (aboutUsSettingKeys.includes(setting.key as AboutUsSettingKey)) {
            const key = setting.key as AboutUsSettingKey;
            const valueField = getValueFieldForKey(key);
            if (valueField === 'value_json') {
                const rawJson = setting.value_json;
                let parsed;
                if(typeof rawJson === 'string') {
                    try { parsed = JSON.parse(rawJson); } catch { parsed = []; }
                } else { parsed = rawJson || []; }
                initialFormState[key] = JSON.stringify(parsed, null, 2);
            } else { initialFormState[key] = (setting[valueField] as string) || ''; }
        }
    });
    aboutUsSettingKeys.forEach(key => {
        if (!(key in initialFormState)) {
             const valueField = getValueFieldForKey(key);
             initialFormState[key] = valueField === 'value_json' ? '[]' : '';
        }
    });
    setSettings(initialFormState);
    setSelectedDirectFiles({}); 
    setUtilityFile(null);
    setUtilityImageUrl(null);
    toast.info("Changes discarded. Form reset to last loaded values.");
  };


  if (isLoading) {
    return <div className="flex justify-center items-center h-screen"><Loader2 className="h-12 w-12 animate-spin text-primary" /> <p className="ml-2">Loading settings...</p></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <header className="flex items-center justify-between gap-4 border-b border-border/60 pb-6 mb-8 relative">
          <div className="flex items-center">
            <FileText className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
            <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Edit <span className="text-gradient-maroon-to-gold">About Us Page</span>
                </h1>
                <p className="mt-1 text-muted-foreground">
                Manage the content displayed on your public "About Us" page.
                </p>
            </div>
          </div>
          <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
      </header>

      {/* General Image Upload Utility Section */}
      <Card className="shadow-lg mb-10">
        <CardHeader>
            <CardTitle><UploadCloud className="inline-block mr-2 h-5 w-5 text-primary" />Image Upload Utility</CardTitle>
            <CardDescription>Upload images here (e.g., for team members, core values). Copy the generated URL and paste it into the appropriate JSON field below.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
            <div>
                <Label htmlFor="utility-image-upload" className="text-sm font-medium">Select Image</Label>
                <Input
                    id="utility-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleUtilityFileChange}
                    className="mt-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-input file:bg-background file:text-sm file:font-medium file:text-foreground hover:file:bg-accent hover:file:text-accent-foreground"
                />
            </div>
            <Button
                type="button"
                onClick={handleUtilityImageUpload}
                disabled={!utilityFile || isUtilityUploading}
                variant="secondary"
            >
                {isUtilityUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                Upload Image & Get URL
            </Button>
            {utilityImageUrl && (
                <div className="mt-3 p-3 border rounded-md bg-muted/50 space-y-2">
                    <p className="text-sm font-medium text-foreground">Uploaded Image URL:</p>
                    <div className="flex items-center gap-2">
                        <Input type="text" value={utilityImageUrl} readOnly className="text-sm bg-background"/>
                        <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => copyToClipboard(utilityImageUrl)}
                            title="Copy URL"
                        >
                            {utilityImageCopied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </Button>
                    </div>
                    <img src={utilityImageUrl} alt="Uploaded utility preview" className="max-w-xs max-h-32 rounded border p-1 object-contain mt-2 bg-white" 
                         onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.style.display = 'none';
                            const errorMsgContainer = target.parentNode?.querySelector('.utility-preview-error-msg');
                            if (errorMsgContainer) errorMsgContainer.remove();
                            const errorMsg = document.createElement('p');
                            errorMsg.textContent = 'Preview unavailable.';
                            errorMsg.className = 'text-xs text-destructive utility-preview-error-msg';
                            target.parentNode?.insertBefore(errorMsg, target.nextSibling);
                        }}
                    />
                </div>
            )}
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-10">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle><TargetIcon className="inline-block mr-2 h-5 w-5 text-primary" />Mission Section</CardTitle>
            <CardDescription>Update the mission statement and related content.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderFormField("about_us_mission_title", "Mission Title")}
            {renderFormField("about_us_mission_p1", "Mission Paragraph 1", undefined, "Enter first paragraph...", "textarea")}
            {renderFormField("about_us_mission_p2", "Mission Paragraph 2", undefined, "Enter second paragraph (optional)...", "textarea")}
            {renderFormField("about_us_mission_image_url", "Mission Image URL", "URL for the image in the mission section. You can also upload an image using the direct uploader for this field.")}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle><Zap className="inline-block mr-2 h-5 w-5 text-primary" />Our Story Section</CardTitle>
            <CardDescription>Edit the company's story.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderFormField("about_us_story_title", "Story Title")}
            {renderFormField("about_us_story_content", "Story Content", undefined, "Tell your company's story...", "textarea")}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
            <CardHeader>
                <CardTitle><LightbulbIcon className="inline-block mr-2 h-5 w-5 text-primary" />Core Values Section</CardTitle>
                <CardDescription>Manage core values as a JSON array of objects. Use the Image Upload Utility above to get URLs for any images.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {renderFormField("about_us_values_title", "Values Section Title")}
                {renderFormField("about_us_core_values_json", "Core Values (JSON)", 'Format: [{"title":"Value1","icon":"IconName","color_class":"text-css","description":"Desc1"}, ...]', undefined, "json_textarea")}
            </CardContent>
        </Card>
        
        <Card className="shadow-lg">
            <CardHeader>
                <CardTitle><UsersIcon className="inline-block mr-2 h-5 w-5 text-primary" />Team / Leadership Section</CardTitle>
                <CardDescription>Manage team members as a JSON array of objects. Use the Image Upload Utility above to get URLs for team member photos.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {renderFormField("about_us_team_title", "Team Section Title")}
                {renderFormField("about_us_team_members_json", "Team Members (JSON)", 'Format: [{"name":"Name1","role":"Role1","imageUrl":"COPIED_URL_HERE","bio":"Bio1"}, ...]', undefined, "json_textarea")}
            </CardContent>
        </Card>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Page Header Customization</CardTitle>
            <CardDescription>Customize the main title and subtitle for the About Us page.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderFormField("about_us_page_title", "Page Title", "e.g., About Our Awesome Company")}
            {renderFormField("about_us_page_subtitle", "Page Subtitle", "e.g., A short tagline or introduction.", undefined, "textarea")}
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-3 sticky bottom-0 py-4 bg-background/80 backdrop-blur-sm border-t border-border">
            <Button type="button" variant="outline" onClick={handleDiscardChanges}>
                Discard Changes
            </Button>
            <Button type="submit" disabled={isSaving || isUtilityUploading || Object.values(isDirectUploading).some(v => v)} className="min-w-[120px]">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditAboutUsPage;
